import { useState, useRef, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import PageHeader from '../components/PageHeader'
import { useAuth } from '../hooks/useAuth'
import { usePractice } from '../hooks/usePractice'
import {
  createRequest, getClients, getSettings,
  sendClientRequestEmail
} from '../lib/supabase'
import { parseCSV, parseRawCsv, formatCurrency, formatDate } from '../lib/csvParser'
import CsvColumnMapper from '../components/CsvColumnMapper'
import { useToast } from '../components/Toast'
import usePageTitle from '../hooks/usePageTitle'

const SOFTWARE_OPTIONS = [
  { value: 'auto',       label: '🔍 Auto-detect' },
  { value: 'xero',       label: 'Xero' },
  { value: 'sage',       label: 'Sage' },
  { value: 'quickbooks', label: 'QuickBooks' },
  { value: 'freeagent',  label: 'FreeAgent' },
  { value: 'custom',     label: '🛠 Custom mapping' },
]

function AmountCell({ amount }) {
  if (amount === null || amount === undefined) return <span style={{ color: 'var(--ink-faint)' }}>—</span>
  const isIncome = amount > 0
  return (
    <span style={{ fontWeight: 600, color: isIncome ? 'var(--green)' : 'var(--ink)' }}>
      {isIncome ? '+' : ''}{formatCurrency(amount)}
    </span>
  )
}

// ─── Reminder controls for the new-request form ───────────────────────────────
//
// Three display states based on the practice and client settings:
//
//   1. Practice has reminders off  -> read-only info banner, no controls
//   2. Client has reminders off    -> read-only info banner, no controls
//   3. Otherwise                   -> per-request toggle plus optional
//                                     "custom schedule for this request" tick
//
// The custom schedule input is hidden by default to keep the new-request
// form uncluttered. Most bookkeepers will accept the practice default.

function ReminderControls({
  practiceRemindersOn, clientRemindersOn, clientName,
  practiceDefaultDays,
  remindersOn, setRemindersOn,
  useCustomSchedule, setUseCustomSchedule,
  customDays, setCustomDays,
}) {
  // State 1: practice-wide off
  if (!practiceRemindersOn) {
    return (
      <ReminderInfoBanner
        title="Auto-reminders are turned off practice-wide"
        body={
          <>
            No reminder will be sent for this request. Turn auto-reminders on
            from <strong>Settings → Auto-reminders</strong> if you'd like to
            change this.
          </>
        }
      />
    )
  }

  // State 2: this specific client opted out
  if (!clientRemindersOn) {
    return (
      <ReminderInfoBanner
        title="Auto-reminders are turned off for this client"
        body={
          <>
            No reminder will be sent for {clientName ? <strong>{clientName}</strong> : 'this client'}.
            You can change this from the client's page.
          </>
        }
      />
    )
  }

  // State 3: the practice and client both allow reminders. Show the
  // per-request controls.
  return (
    <div className="card" style={{ padding: '16px 20px', marginBottom: 24 }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
        }}
        onClick={() => setRemindersOn(v => !v)}
      >
        <div style={{
          width: 36, height: 20, borderRadius: 100, flexShrink: 0,
          background: remindersOn ? 'var(--green)' : 'var(--surface-3)',
          position: 'relative', transition: 'background 0.2s',
        }}>
          <div style={{
            width: 14, height: 14, borderRadius: '50%', background: 'white',
            position: 'absolute', top: 3,
            left: remindersOn ? 19 : 3,
            transition: 'left 0.2s',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }} />
        </div>
        <div style={{ lineHeight: 1.4 }}>
          <span style={{ fontWeight: 500, color: 'var(--ink)', fontSize: '0.9rem' }}>
            Send an auto-reminder if this request stays incomplete
          </span>
          <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', marginTop: 2 }}>
            {remindersOn
              ? (useCustomSchedule
                  ? `Reminds the client after ${customDays || 'N'} day${customDays === '1' ? '' : 's'}`
                  : `Uses your practice default of ${practiceDefaultDays} day${practiceDefaultDays === 1 ? '' : 's'}`)
              : 'No reminder will be sent for this request'}
          </div>
        </div>
      </div>

      {/* Custom schedule, hidden by default to keep the form clean */}
      {remindersOn && (
        <div style={{
          marginTop: 14, paddingTop: 14,
          borderTop: '1px solid var(--surface-2)',
        }}>
          <label
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              cursor: 'pointer', fontSize: '0.84rem', color: 'var(--ink-muted)',
            }}
          >
            <input
              type="checkbox"
              checked={useCustomSchedule}
              onChange={e => setUseCustomSchedule(e.target.checked)}
              style={{ accentColor: 'var(--accent)' }}
            />
            Use a different schedule for this request
          </label>

          {useCustomSchedule && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              marginTop: 10,
            }}>
              <input
                type="number"
                min={1}
                max={90}
                value={customDays}
                onChange={e => setCustomDays(e.target.value)}
                className="input"
                style={{ width: 90 }}
              />
              <span style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
                days after the request is sent
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ReminderInfoBanner({ title, body }) {
  return (
    <div style={{
      padding: '14px 18px', marginBottom: 24,
      background: 'var(--surface)', border: '1px solid var(--surface-2)',
      borderRadius: 'var(--radius)', display: 'flex', gap: 12, alignItems: 'flex-start',
    }}>
      <span style={{ fontSize: '1rem', lineHeight: 1.4 }}>ℹ️</span>
      <div style={{ fontSize: '0.85rem', lineHeight: 1.55 }}>
        <div style={{ fontWeight: 500, color: 'var(--ink)', marginBottom: 2 }}>{title}</div>
        <div style={{ color: 'var(--ink-muted)' }}>{body}</div>
      </div>
    </div>
  )
}

export default function NewRequest() {
  const { clientId } = useParams()
  const { user } = useAuth()
  const { practiceId } = usePractice()
  const navigate = useNavigate()
  const toast = useToast()
  usePageTitle('New request')
  const fileRef = useRef()

  const [step, setStep]                       = useState(1)
  const [software, setSoftware]               = useState('auto')
  const [title, setTitle]                     = useState('')
  const [bookkeeperNotes, setBookkeeperNotes] = useState('')
  const [transactions, setTransactions]       = useState([])
  const [parseInfo, setParseInfo]             = useState(null)
  const [parsing, setParsing]                 = useState(false)

  // ─── Custom mapping state ────────────────────────────────────────────────
  // When the user picks 'custom' from the software dropdown, or when auto-
  // detect comes back with zero rows, we drop the file's raw rows into
  // mappingRows and render the CsvColumnMapper component in place of the
  // upload area. The mapper calls back with parsed transactions which we
  // then feed into the existing step-2 flow.
  const [mappingRows, setMappingRows]         = useState(null)
  const [saving, setSaving]                   = useState(false)
  const [dragOver, setDragOver]               = useState(false)
  const [deliveryMethod, setDeliveryMethod]   = useState('email')
  const [sending, setSending]                 = useState(false)
  const [client, setClient]                   = useState(null)
  const [settings, setSettings]               = useState(null)

  // ─── Per-request reminder override ──────────────────────────────────────────
  // remindersOn        : whether this request gets a reminder
  // useCustomSchedule  : true if the user wants to override the practice default
  // customDays         : the override day count (only used when useCustomSchedule)
  const [remindersOn, setRemindersOn]                   = useState(true)
  const [useCustomSchedule, setUseCustomSchedule]       = useState(false)
  const [customDays, setCustomDays]                     = useState('7')

  useEffect(() => {
    if (!user || !practiceId) return
    getClients(practiceId).then(({ data }) => {
      setClient(data?.find(x => x.id === clientId) || null)
    })
    getSettings(practiceId).then(({ data }) => {
      setSettings(data || {})
      // Pre-populate the per-request reminder default day input with the
      // practice's chosen default. The user can override it for this request
      // only by ticking the custom schedule checkbox.
      if (data?.reminder_days_after_send) {
        setCustomDays(String(data.reminder_days_after_send))
      }
    })
  }, [user, practiceId, clientId])

  const handleFile = async (f) => {
    if (!f || !f.name.endsWith('.csv')) {
      toast('Please upload a .csv file', 'error')
      return
    }
    setParsing(true)
    try {
      // Two paths into the mapper:
      //   1. User explicitly picked 'custom' from the software dropdown
      //   2. They left it on auto-detect (or picked a specific format), but
      //      parsing produced zero usable rows: we offer the mapper as a
      //      fallback rather than just showing an error.
      if (software === 'custom') {
        const { rows } = await parseRawCsv(f)
        setMappingRows(rows)
        setParsing(false)
        return
      }

      const result = await parseCSV(f, software)
      if (!result.transactions || result.transactions.length === 0) {
        // Auto-detect (or chosen format) extracted nothing. Fall back to
        // the mapper using the raw rows already parsed.
        toast(
          "Couldn't read this file automatically. Map the columns yourself below.",
          'default'
        )
        setMappingRows(result.rawRows || [])
        setParsing(false)
        return
      }

      setTransactions(result.transactions)
      setParseInfo(result)
      if (!title) setTitle(`Receipt request for ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`)
      setStep(2)
    } catch (err) {
      toast('Could not parse CSV: ' + err.message, 'error')
    }
    setParsing(false)
  }

  // Called when the user confirms a custom mapping. The CsvColumnMapper has
  // already produced the transactions; we just need to feed them into the
  // existing step-2 flow and clear the mapping state.
  const handleMappingApply = (transactions, mapping) => {
    setTransactions(transactions)
    setParseInfo({
      software: 'custom',
      rowCount: transactions.length,
      mapping,
    })
    if (!title) setTitle(`Receipt request for ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`)
    setMappingRows(null)
    setStep(2)
  }

  const handleMappingCancel = () => {
    setMappingRows(null)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    handleFile(e.dataTransfer.files[0])
  }

  const removeTransaction = (index) => {
    setTransactions(prev => prev.filter((_, i) => i !== index))
  }

  const handleCreate = async () => {
    if (!title.trim()) { toast('Please enter a title', 'error'); return }
    if (transactions.length === 0) { toast('No transactions to send — please add at least one', 'error'); return }

    // Build reminder options. Don't pass daysAfterSend unless the user
    // ticked "use a different schedule for this request"; otherwise let it
    // default to the practice schedule (null in the DB).
    const reminderOpts = { reminderEnabled: remindersOn }
    if (remindersOn && useCustomSchedule) {
      const days = parseInt(customDays, 10)
      if (!Number.isNaN(days) && days >= 1 && days <= 90) {
        reminderOpts.reminderDaysAfterSend = days
      }
    }

    setSaving(true)
    const { data, error } = await createRequest(clientId, practiceId, title, bookkeeperNotes, transactions, reminderOpts)
    setSaving(false)
    if (error) { toast(error.message, 'error'); return }

    const link = `${window.location.origin}/request/${data.token}`

    if (deliveryMethod === 'email') {
      if (!client?.email) { toast('No email address on file for this client', 'error'); return }
      setSending(true)
      const { error: emailError } = await sendClientRequestEmail({
        clientEmail:     client.email,
        clientName:      client.name,
        // Fallback chain mirrors the reminder worker: prefer display_name,
        // then practice_name, then full_name. Never fall back to user.email,
        // since that leaks the bookkeeper's email address into the client-
        // facing email body.
        bookkeeperName:  settings?.display_name
                      || settings?.practice_name
                      || settings?.full_name
                      || 'Your bookkeeper',
        requestTitle:    title,
        requestLink:     link,
        subjectTemplate: settings?.email_subject_template || null,
        bodyTemplate:    settings?.email_body_template || null,
      })
      setSending(false)
      if (emailError) {
        toast('Request created but email failed: ' + emailError.message, 'error')
      } else {
        toast(`Request created and email sent to ${client.email} ✓`, 'success')
      }
    } else {
      await navigator.clipboard.writeText(link).catch(() => {})
      toast('Request created! Link copied to clipboard ✓', 'success')
    }

    // Land on the new request page so the bookkeeper can review and
    // edit the transactions, descriptions, notes, or reminder schedule
    // they just sent out. Previously this landed back on the client
    // page, which made any follow-up edits an extra navigation step.
    navigate(`/requests/${data.id}`)
  }

  const incomeCount      = transactions.filter(t => t.amount > 0).length
  const expenditureCount = transactions.filter(t => t.amount <= 0).length

  return (
    <>
      <Navbar />
      <PageHeader
        back={{ label: 'Back to client', to: `/clients/${clientId}` }}
        title="New receipt request"
        subtitle="Upload a CSV export from your accounting software to create a request."
      />
      <main className="container" style={{ padding: '0 24px 40px' }}>

        {/* Steps indicator */}
        <div className="fade-up" style={{ display: 'flex', gap: 8, marginBottom: 32, alignItems: 'center' }}>
          {[1, 2].map(n => (
            <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: step >= n ? 'var(--accent)' : 'var(--surface-3)',
                color: step >= n ? 'white' : 'var(--ink-muted)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', fontWeight: 600, transition: 'all 0.2s',
              }}>{n}</div>
              <span style={{ fontSize: '0.85rem', color: step >= n ? 'var(--ink)' : 'var(--ink-faint)', fontWeight: step === n ? 500 : 400 }}>
                {n === 1 ? 'Upload CSV' : 'Review & send'}
              </span>
              {n < 2 && <span style={{ color: 'var(--ink-faint)', margin: '0 4px' }}>›</span>}
            </div>
          ))}
        </div>

        {/* ── Step 1: Upload (or custom mapping if active) ────────────────── */}
        {step === 1 && mappingRows && (
          <div className="fade-up-2">
            <CsvColumnMapper
              rows={mappingRows}
              onCancel={handleMappingCancel}
              onApply={handleMappingApply}
            />
          </div>
        )}
        {step === 1 && !mappingRows && (
          <div className="fade-up-2">
            <div className="card" style={{ padding: 32, maxWidth: 600 }}>
              <div className="form-group">
                <label>Accounting software</label>
                <select className="input" value={software} onChange={e => setSoftware(e.target.value)}>
                  {SOFTWARE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginTop: 6 }}>
                  Auto-detect works for most exports. Select your software for best results.
                </p>
              </div>

              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                style={{
                  border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--surface-3)'}`,
                  borderRadius: 'var(--radius-lg)', padding: '48px 24px',
                  textAlign: 'center', cursor: 'pointer',
                  background: dragOver ? 'var(--accent-light)' : 'var(--surface)',
                  transition: 'all 0.2s',
                }}
              >
                <input ref={fileRef} type="file" accept=".csv" hidden onChange={e => handleFile(e.target.files[0])} />
                {parsing ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    <div className="spinner" />
                    <span style={{ color: 'var(--ink-muted)', fontSize: '0.9rem' }}>Parsing CSV…</span>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📂</div>
                    <div style={{ fontWeight: 500, marginBottom: 6 }}>Drop your CSV here</div>
                    <div style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>or click to browse</div>
                    <div style={{ marginTop: 16, color: 'var(--ink-faint)', fontSize: '0.78rem' }}>
                      Supports Xero · Sage · QuickBooks · FreeAgent exports
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Review & send ───────────────────────────────────────── */}
        {step === 2 && (
          <div className="fade-up-2">

            {/* Parse banner */}
            {parseInfo && (
              <div style={{
                background: 'var(--green-light)', border: '1.5px solid var(--green)',
                borderRadius: 'var(--radius)', padding: '12px 18px',
                fontSize: '0.85rem', color: 'var(--green)', marginBottom: 20,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                ✓{' '}
                {parseInfo.software === 'custom'
                  ? <>Custom mapping applied. {transactions.length} transactions ready.</>
                  : <>Detected <strong>{parseInfo.software}</strong> format. {transactions.length} transactions.</>
                }
                {incomeCount > 0 && <span>· {incomeCount} income</span>}
                {expenditureCount > 0 && <span style={{ color: 'var(--ink-muted)' }}>· {expenditureCount} expenditure</span>}
              </div>
            )}

            {/* Title + notes */}
            <div className="card" style={{ padding: 24, marginBottom: 20, maxWidth: 680 }}>
              <div className="form-group">
                <label>Request title (shown to your client)</label>
                <input
                  className="input"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="e.g. Q1 2024 missing receipts"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Notes for client <span style={{ color: 'var(--ink-faint)', fontWeight: 400, textTransform: 'none', fontSize: '0.8rem' }}>(optional)</span></label>
                <textarea
                  className="input"
                  value={bookkeeperNotes}
                  onChange={e => setBookkeeperNotes(e.target.value)}
                  placeholder="e.g. Please upload receipts for all items below. If you can't find a receipt, use the 'Mark as lost' option."
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>
            </div>

            {/* ── Reminder controls ───────────────────────────────────────── */}
            {/* Three display states:
                  1. Practice has reminders off -> info banner, no controls
                  2. Client has reminders off   -> info banner, no controls
                  3. Otherwise                  -> toggle + optional custom schedule */}
            <ReminderControls
              practiceRemindersOn={settings?.reminders_enabled !== false}
              clientRemindersOn={client?.reminders_enabled !== false}
              clientName={client?.name}
              practiceDefaultDays={settings?.reminder_days_after_send ?? 7}
              remindersOn={remindersOn}
              setRemindersOn={setRemindersOn}
              useCustomSchedule={useCustomSchedule}
              setUseCustomSchedule={setUseCustomSchedule}
              customDays={customDays}
              setCustomDays={setCustomDays}
            />

            {/* Transaction table with remove buttons */}
            <div className="card" style={{ marginBottom: 24 }}>
              <div style={{ padding: '16px 20px', borderBottom: '1.5px solid var(--surface-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 500 }}>
                  {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
                </span>
                <button className="btn btn-ghost btn-sm" onClick={() => setStep(1)}>← Change CSV</button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Type</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th style={{ width: 36 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx, i) => (
                      <tr key={i}>
                        <td style={{ color: 'var(--ink-muted)', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{formatDate(tx.date)}</td>
                        <td style={{ maxWidth: 320 }}>{tx.description}</td>
                        <td>
                          <span style={{
                            fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: 100,
                            background: tx.amount > 0 ? 'var(--green-light)' : 'var(--surface-2)',
                            color: tx.amount > 0 ? 'var(--green)' : 'var(--ink-muted)',
                          }}>
                            {tx.amount > 0 ? 'Income' : 'Expenditure'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <AmountCell amount={tx.amount} />
                        </td>
                        <td>
                          <button
                            className="btn btn-ghost btn-sm btn-icon"
                            onClick={() => removeTransaction(i)}
                            title="Remove this transaction"
                            style={{ color: '#dc2626' }}
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {transactions.length === 0 && (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ink-faint)', fontSize: '0.88rem' }}>
                  All transactions removed. <button className="btn btn-ghost btn-sm" onClick={() => setStep(1)} style={{ display: 'inline' }}>← Go back to upload a new CSV</button>
                </div>
              )}
            </div>

            {/* Delivery method */}
            <div className="card" style={{ padding: 24, marginBottom: 24, maxWidth: 680 }}>
              <label style={{ marginBottom: 12, display: 'block' }}>
                How would you like to send this to {client?.name || 'your client'}?
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div
                  onClick={() => setDeliveryMethod('email')}
                  style={{
                    padding: '16px 20px', borderRadius: 'var(--radius)', cursor: 'pointer',
                    border: `2px solid ${deliveryMethod === 'email' ? 'var(--accent)' : 'var(--surface-3)'}`,
                    background: deliveryMethod === 'email' ? 'var(--accent-light)' : 'var(--white)',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ fontSize: '1.4rem', marginBottom: 8 }}>📧</div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 4 }}>Send email automatically</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', lineHeight: 1.5 }}>
                    Receipt Chaser emails the request directly to {client?.email || 'the client\'s email address'}. You can customise the email template in <a href="/settings" onClick={e => e.stopPropagation()} style={{ color: 'var(--accent)' }}>Settings</a>.
                  </div>
                  {!client?.email && (
                    <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: 6 }}>
                      ⚠ No email address on file for this client
                    </div>
                  )}
                </div>

                <div
                  onClick={() => setDeliveryMethod('link')}
                  style={{
                    padding: '16px 20px', borderRadius: 'var(--radius)', cursor: 'pointer',
                    border: `2px solid ${deliveryMethod === 'link' ? 'var(--accent)' : 'var(--surface-3)'}`,
                    background: deliveryMethod === 'link' ? 'var(--accent-light)' : 'var(--white)',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ fontSize: '1.4rem', marginBottom: 8 }}>🔗</div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 4 }}>Copy link to send myself</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', lineHeight: 1.5 }}>
                    The link is copied to your clipboard so you can paste it into your own email or message.
                  </div>
                </div>
              </div>

              {deliveryMethod === 'email' && settings && !settings.display_name && (
                <div style={{
                  marginTop: 14, padding: '10px 14px', borderRadius: 'var(--radius)',
                  background: 'var(--amber-light)', fontSize: '0.8rem', color: 'var(--amber)',
                }}>
                  💡 Add your display name in <a href="/settings" style={{ color: 'var(--amber)', fontWeight: 600 }}>Settings</a> so clients see your name rather than your email address.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-secondary" onClick={() => setStep(1)}>← Back</button>
              <button
                className="btn btn-primary btn-lg"
                onClick={handleCreate}
                disabled={saving || sending || transactions.length === 0 || (deliveryMethod === 'email' && !client?.email)}
              >
                {saving ? 'Creating…' : sending ? 'Sending email…' : deliveryMethod === 'email' ? '✓ Create & send email' : '✓ Create & copy link'}
              </button>
            </div>
          </div>
        )}
      </main>
    </>
  )
}
